import React, { useState } from "react";
import { Group, Stack, Table, Text, UnstyledButton } from "@mantine/core";
import { Badge, Button } from "@xplatform/design-system";
import { IconChevronDown, IconChevronRight, IconDownload, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { formatMb } from "@utils/download";
import type { HistoryEvent } from "@models/reviews";
import { groupUploads, INACTIVE_STATES, type UploadRow, type UploadState } from "./uploadHistory";

const STATE_COLOR: Record<UploadState, string> = {
  pending: "yellow",
  imported: "teal",
  superseded: "gray",
  cancelled: "gray",
  failed: "red",
  unknown: "gray",
};

interface Props {
  events: HistoryEvent[];
  downloadingKey: string | null;
  onDownload: (fileKey: string) => void;
  onCancel: (fileKey: string) => void;
}

const when = (ts: string) => (ts ? new Date(ts).toLocaleString() : "—");

/**
 * Upload history of one review file (PBI #781340): one row per upload with its latest
 * status, newest first; uploads that are no longer current are greyed out. The raw event
 * log is available collapsed underneath.
 */
export const UploadHistoryTable: React.FC<Props> = ({ events, downloadingKey, onDownload, onCancel }) => {
  const { t } = useTranslation();
  const [showEvents, setShowEvents] = useState(false);
  const uploads = groupUploads(events);

  const statusDetail = (u: UploadRow): string | null => {
    const e = u.latest;
    switch (u.state) {
      case "superseded":
        return e.verdicts_changed != null
          ? t("reviews.history.supersededDetailCounts", { when: when(e.ts), by: e.actor, changed: e.verdicts_changed, total: e.verdicts_total })
          : t("reviews.history.supersededDetail", { when: when(e.ts), by: e.actor });
      case "cancelled":
        return t("reviews.history.cancelledDetail", { when: when(e.ts), by: e.actor });
      case "imported":
        return t("reviews.history.importedDetail", { when: when(e.ts), count: e.inserted ?? 0 });
      case "failed":
        return t("reviews.history.failedDetail", { when: when(e.ts), reason: e.failure_reason ?? "—" });
      default:
        return null;
    }
  };

  return (
    <Stack gap="sm">
      <Table style={{ tableLayout: "fixed", width: "100%" }} data-testid="upload-history">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("reviews.history.uploadedBy")}</Table.Th>
            <Table.Th style={{ width: "170px" }}>{t("reviews.history.uploadedAt")}</Table.Th>
            <Table.Th style={{ width: "70px" }}>{t("reviews.history.rows")}</Table.Th>
            <Table.Th style={{ width: "300px" }}>{t("reviews.history.status")}</Table.Th>
            <Table.Th style={{ width: "90px" }}>{t("reviews.history.size")}</Table.Th>
            {/* Wide enough for Download + Cancel side by side; they wrap rather than truncate. */}
            <Table.Th style={{ width: "260px" }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {uploads.map((u) => {
            const inactive = INACTIVE_STATES.has(u.state);
            const detail = statusDetail(u);
            // Only an upload still waiting in reviews/upload/ can be cancelled.
            const cancellable = u.state === "pending" && !!u.fileKey?.startsWith("reviews/upload/");
            return (
              <Table.Tr key={u.uploadKey} style={inactive ? { opacity: 0.55 } : undefined} data-state={u.state}>
                <Table.Td>{u.uploadedBy}</Table.Td>
                <Table.Td>{when(u.uploadedAt)}</Table.Td>
                <Table.Td>{u.rows ?? "—"}</Table.Td>
                <Table.Td>
                  <Badge color={STATE_COLOR[u.state]} variant="light" size="sm">
                    {t(`reviews.history.states.${u.state}`, { action: u.latest.action })}
                  </Badge>
                  {detail && (
                    <Text size="xs" c="dimmed" mt={2}>
                      {detail}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{u.size != null ? formatMb(u.size) : "—"}</Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="wrap">
                    {u.fileKey && u.size != null && (
                      <Button
                        size="xs"
                        variant="subtle"
                        leftSection={<IconDownload size={12} />}
                        loading={downloadingKey === u.fileKey}
                        onClick={() => onDownload(u.fileKey as string)}
                      >
                        {t("reviews.detail.download")}
                      </Button>
                    )}
                    {cancellable && (
                      <Button
                        size="xs"
                        color="red"
                        variant="subtle"
                        leftSection={<IconTrash size={12} />}
                        onClick={() => onCancel(u.fileKey as string)}
                      >
                        {t("reviews.upload.cancelUpload")}
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed">
        {t("reviews.detail.allRowsNote")}
      </Text>

      <UnstyledButton onClick={() => setShowEvents((v) => !v)} aria-expanded={showEvents}>
        <Group gap={4}>
          {showEvents ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <Text size="sm" c="dimmed">
            {t(showEvents ? "reviews.history.hideEvents" : "reviews.history.showEvents", { count: events.length })}
          </Text>
        </Group>
      </UnstyledButton>
      {showEvents && (
        <Table striped style={{ tableLayout: "fixed", width: "100%" }} data-testid="event-log">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "170px" }}>{t("reviews.detail.when")}</Table.Th>
              <Table.Th style={{ width: "140px" }}>{t("reviews.detail.action")}</Table.Th>
              <Table.Th>{t("reviews.detail.by")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {events.map((e, i) => (
              <Table.Tr key={`${e.ts}-${e.action}-${i}`}>
                <Table.Td>{when(e.ts)}</Table.Td>
                <Table.Td>{t(`reviews.detail.actions.${e.action}`, { defaultValue: e.action })}</Table.Td>
                <Table.Td>{e.actor}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
};
